import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { detectCountry, getLanguageForCountry } from '../utils/geolocation';
import './LanguageSuggestionBanner.css';

const LanguageSuggestionBanner = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [isVisible, setIsVisible] = useState(false);
  const [prevLang, setPrevLang] = useState(null);
  const [targetLang, setTargetLang] = useState(null);

  useEffect(() => {
    // Check if geo check has already been performed
    const geoChecked = localStorage.getItem('pawfriends_geo_checked');
    if (geoChecked === 'true') {
      return;
    }

    const performGeoCheck = async () => {
      try {
        const countryCode = await detectCountry();
        // Set the checked flag immediately so we don't query the API again on navigation or refresh
        localStorage.setItem('pawfriends_geo_checked', 'true');

        if (!countryCode) return;

        const detectedLang = getLanguageForCountry(countryCode);
        if (!detectedLang) return;

        // Determine browser-preferred/default language
        const preferredLang = i18n.language ? (i18n.language.split('-')[0].toLowerCase() === 'bs' ? 'bs' : 'en') : 'en';

        if (detectedLang !== preferredLang) {
          console.log(`[Geolocation] Auto-switching language from browser preferred ${preferredLang} to detected ${detectedLang} based on country ${countryCode}`);
          
          setPrevLang(preferredLang);
          setTargetLang(detectedLang);

          // Adjust path prefix
          let cleanPath = location.pathname;
          if (location.pathname.startsWith('/en')) {
            cleanPath = location.pathname.substring(3);
          }
          if (!cleanPath) cleanPath = '/';

          let newPath;
          if (detectedLang === 'bs') {
            newPath = cleanPath;
          } else {
            newPath = `/en${cleanPath === '/' ? '' : cleanPath}`;
          }

          // Change the language in i18n and navigate to the new path prefix
          i18n.changeLanguage(detectedLang);
          navigate(newPath + location.search, { replace: true });

          // Show the banner
          setIsVisible(true);
        }
      } catch (err) {
        console.error('[Geolocation] Error performing geo check:', err);
      } finally {
        window.dispatchEvent(new Event('pawfriends_geo_complete'));
      }
    };

    performGeoCheck();
  }, [i18n, location.pathname, location.search, navigate]);

  // If the language changes to something other than the target language (e.g. manual toggle), hide the banner
  useEffect(() => {
    const currentLang = location.pathname.startsWith('/en') ? 'en' : 'bs';
    if (isVisible && targetLang && currentLang !== targetLang) {
      setIsVisible(false);
    }
  }, [location.pathname, isVisible, targetLang]);

  // Auto-dismiss the banner after 10 seconds
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 10000); // 10 seconds
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleSwitchBack = () => {
    if (!prevLang) return;

    let cleanPath = location.pathname;
    if (location.pathname.startsWith('/en')) {
      cleanPath = location.pathname.substring(3);
    }
    if (!cleanPath) cleanPath = '/';

    let newPath;
    if (prevLang === 'bs') {
      newPath = cleanPath;
    } else {
      newPath = `/en${cleanPath === '/' ? '' : cleanPath}`;
    }

    i18n.changeLanguage(prevLang);
    navigate(newPath + location.search);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="pf-geo-banner">
      <div className="pf-geo-banner-content">
        <span className="pf-geo-banner-text">
          <i className="fas fa-globe-europe pf-geo-banner-globe-icon"></i>
          {t('locationBanner.switched')}
        </span>
        <div className="pf-geo-banner-actions">
          <button onClick={handleSwitchBack} className="pf-geo-banner-btn-revert">
            {t('locationBanner.switchBack')}
          </button>
          <button onClick={handleDismiss} className="pf-geo-banner-btn-dismiss">
            {t('locationBanner.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSuggestionBanner;
